'use client';

import React from 'react';
import { Shield, AlertTriangle, Scale, Users, CreditCard, Database, Globe, Lock } from 'lucide-react';
import Footer from '@/components/Footer';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Scale className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Terms and Conditions</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Last updated: August 28, 2026
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="h-6 w-6 text-blue-600 mr-2" />
              1. Introduction
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                Welcome to SmartMetrics ("we," "our," or "us"). These Terms and Conditions ("Terms") 
                govern your use of our agency management platform and related services, including client proposal 
                management, payment processing, subscription management, booking widgets, client portals, and 
                related business management tools (collectively, the "Services").
              </p>
              <p>
                By accessing or using our Services, creating an account, or making a payment, you agree to be bound 
                by these Terms. If you disagree with any part of these terms, you may not access the Services.
              </p>
            </div>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 text-blue-600 mr-2" />
              2. Service Description
            </h2>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.1 Agency Management Platform</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Client Management:</strong> Manage client relationships, contacts, and project information</li>
                <li><strong>Proposal Creation:</strong> Create, customize, and share client proposals with pricing and service details</li>
                <li><strong>Contract Management:</strong> Generate and manage contracts for one-time projects and recurring services</li>
                <li><strong>Payment Processing:</strong> Accept payments via Stripe for one-time projects and recurring subscriptions</li>
                <li><strong>Client Portals:</strong> Provide clients with access to proposals, contracts, shared files, and project information</li>
                <li><strong>Booking Widgets:</strong> Enable clients to schedule meetings and consultations</li>
                <li><strong>Team Collaboration:</strong> Manage team members, assign tasks, and track project progress</li>
                <li><strong>Subscription Management:</strong> Manage agency subscription plans with monthly or annual billing</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.2 Payment and Billing Services</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe Integration:</strong> Secure payment processing for one-time and recurring payments</li>
                <li><strong>Subscription Billing:</strong> Automated monthly or annual subscription billing for agency plans</li>
                <li><strong>One-Time Payments:</strong> Payment processing for individual projects and services</li>
                <li><strong>Payment Security:</strong> All payments are processed securely through Stripe's payment infrastructure</li>
              </ul>
            </div>
          </section>

          {/* Account and Registration */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 text-blue-600 mr-2" />
              3. Account and Registration
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                To access our Services, you must create an account by providing your email address and creating a password, 
                or by signing in with Google OAuth. You may also use our magic link authentication feature. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, complete, and current information during registration</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use or security breach</li>
                <li>Ensuring your contact information remains current and accurate</li>
                <li>Using a strong password (minimum 8 characters) and keeping it secure</li>
                <li>Not sharing your account credentials with third parties</li>
              </ul>
              <p className="mt-4">
                You are responsible for maintaining the security of your account. We are not liable for any loss or 
                damage arising from your failure to protect your account credentials.
              </p>
            </div>
          </section>

          {/* Usage and Billing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-6 w-6 text-blue-600 mr-2" />
              4. Usage, Billing, and Subscriptions
            </h2>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 Agency Subscription Plans</h3>
              <p>
                Our Services operate on subscription-based pricing. Agency plans are available with monthly or annual billing 
                intervals. Annual plans are offered at a discounted rate (typically 20% off monthly pricing). Subscription 
                plans include limits on the number of team members (seats) and clients you can manage.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">4.2 Payment Processing</h3>
              <p>
                All payments are processed securely through Stripe, a third-party payment processor. By making a payment, 
                you agree to Stripe's terms of service. We accept major credit cards and other payment methods supported 
                by Stripe. Payment information is encrypted and stored securely by Stripe, not by SmartMetrics.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">4.3 Billing and Payment Terms</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Subscription Billing:</strong> Agency subscription fees are billed in advance on a monthly or annual basis, 
                depending on your selected billing interval.</li>
                <li><strong>One-Time Payments:</strong> Payments for individual projects or services are charged immediately upon 
                completion of the payment process.</li>
                <li><strong>Recurring Payments:</strong> For recurring service subscriptions, payments are automatically charged 
                on a monthly basis until cancelled.</li>
                <li><strong>Payment Authorization:</strong> By providing payment information, you authorize us to charge the 
                payment method for all fees associated with your account.</li>
                <li><strong>Failed Payments:</strong> If a payment fails, we may suspend your account until payment is successfully 
                processed. You are responsible for ensuring your payment method remains valid.</li>
                <li><strong>Price Changes:</strong> We reserve the right to modify pricing with 30 days' notice. Your existing 
                subscription will continue at the current rate until the next billing cycle.</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">4.4 Refunds and Cancellations</h3>
              <p className="mb-4">Cancellation and refund terms vary depending on the type of service purchased:</p>

              <h4 className="text-base font-semibold text-gray-900 mb-2">SaaS Subscriptions</h4>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Cancellation:</strong> You may cancel your subscription at any time directly through your account 
                dashboard. No email or manual request is required.</li>
                <li><strong>Effect of Cancellation:</strong> Cancellations take effect at the end of the current billing period. 
                You will retain full access to paid features until that date.</li>
                <li><strong>Refunds:</strong> No refunds are provided for partial billing periods except as required by applicable law.</li>
              </ul>

              <h4 className="text-base font-semibold text-gray-900 mb-2">One-Time Projects</h4>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Refund Eligibility:</strong> A full refund may be issued only if SmartMetrics has not yet invested 
                any time toward your project. Once any time has been spent — including but not limited to introductory or 
                scoping meetings, audits, strategy sessions, or any actual project work — the payment becomes non-refundable.</li>
                <li><strong>How to Request:</strong> To request a refund, contact us at support@smartmetrics.com before any 
                time has been invested on your project.</li>
              </ul>

              <h4 className="text-base font-semibold text-gray-900 mb-2">Retainer Agreements</h4>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Cancellation:</strong> Retainer agreements require at least 30 days' advance written notice 
                of cancellation, sent by email to support@smartmetrics.com. Notice is effective on the date the email is 
                received by SmartMetrics.</li>
                <li><strong>Effect of Cancellation:</strong> The 30-day notice period begins on the date we receive your 
                notice. Cancellation takes effect at the end of the billing cycle in which that notice period ends, and 
                services continue through that date. You remain responsible for all fees through the effective date of 
                cancellation, after which no further work will be performed and no further charges will apply.</li>
                <li><strong>No Proration:</strong> Fees are not prorated. If the 30-day notice period extends past the end 
                of your current billing cycle, the following full monthly cycle will be charged in full.</li>
                <li><strong>Short Spillover:</strong> If the 30-day notice period would end within the first 10 days of a 
                new billing cycle, cancellation instead takes effect at the end of the billing cycle that was in progress 
                when we received your notice, and that new cycle will not be charged.</li>
                <li><strong>Termination by SmartMetrics:</strong> SmartMetrics may terminate a retainer at any time, with 
                or without cause, effective immediately upon written notice. The 30-day notice requirement does not apply 
                to SmartMetrics. If we terminate for convenience, we will refund any prepaid fees covering services not 
                yet performed, prorated on a daily basis. No refund is issued where we terminate for non-payment, breach, 
                or abusive, fraudulent, or unlawful conduct.</li>
                <li><strong>Refunds:</strong> Except where SmartMetrics terminates for convenience, no refunds are issued 
                for any billing cycle, including the cycle in which cancellation takes effect.</li>
              </ul>

              <h4 className="text-base font-semibold text-gray-900 mb-2">General</h4>
              <ul className="list-disc pl-6 space-y-2">
                
                <li><strong>Refund Processing:</strong> Approved refunds will be processed to the original payment method 
                within 5–10 business days.</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">4.5 Service Limits</h3>
              <p>
                Service limits vary by subscription plan and include restrictions on the number of team members, clients, 
                proposals, and other features. Current limits are detailed in your account dashboard and plan description. 
                Exceeding plan limits may require upgrading to a higher tier plan.
              </p>
            </div>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 text-blue-600 mr-2" />
              5. Acceptable Use Policy
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>You agree not to use our Services for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Any unlawful purpose or in violation of any laws</li>
                <li>Spam, phishing, or other malicious activities</li>
                <li>Scraping or harvesting data without permission</li>
                <li>Attempting to gain unauthorized access to our systems</li>
                <li>Interfering with or disrupting our Services</li>
                <li>Reverse engineering or attempting to extract our algorithms</li>
                <li>Reselling our Services without written permission</li>
                <li>Violating any third-party rights or privacy</li>
                <li>Sharing SmartMetrics Alerts subscription access with unauthorized users</li>
                <li>Using false or misleading information in white-label services</li>
                <li>Circumventing service limits or usage restrictions</li>
              </ul>
            </div>
          </section>

          {/* Data and Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 text-blue-600 mr-2" />
              6. Data and Privacy
            </h2>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.1 Data Processing</h3>
              <p>
                We process data in accordance with our Privacy Policy. By using our Services, you consent to the 
                collection, processing, and storage of data as described therein.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">6.2 Data Retention</h3>
              <p>
                We retain API request data for analytics and service improvement purposes. Personal data is retained 
                in accordance with applicable privacy laws and our data retention policy.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">6.3 Third-Party Integrations</h3>
              <p>
                Our Services integrate with third-party platforms including Google Analytics, Google Ads, Meta, LinkedIn, 
                and other marketing platforms. We are not responsible for the availability, accuracy, or policies of these 
                third-party services. You are responsible for maintaining valid access to integrated platforms.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">6.4 Data Accuracy</h3>
              <p>
                While we strive to provide accurate insights and recommendations, SmartMetrics Alerts are based on 
                automated analysis and may not capture all relevant factors. You should verify important information 
                and use alerts as guidance rather than definitive business decisions.
              </p>
            </div>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-6 w-6 text-blue-600 mr-2" />
              7. Service Availability and Modifications
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                We strive to maintain high service availability but do not guarantee uninterrupted access. 
                We may modify, suspend, or discontinue any part of our Services at any time with reasonable notice.
              </p>
              <p>
                We reserve the right to perform maintenance, updates, and improvements that may temporarily affect 
                service availability.
              </p>
            </div>
          </section>

          {/* Account Suspension and Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              8. Account Suspension and Termination
            </h2>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">8.1 Our Rights</h3>
              <p>We may suspend or terminate your account immediately if you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate these Terms or our Acceptable Use Policy</li>
                <li>Engage in fraudulent or illegal activities</li>
                <li>Fail to pay fees when due</li>
                <li>Abuse our Services or infrastructure</li>
                <li>Pose a security risk to our systems or other users</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">8.2 API Key Revocation</h3>
              <p>
                We reserve the right to revoke API keys at any time for violations of these Terms, security concerns, 
                or at our sole discretion. Revoked keys will immediately cease to function.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">8.3 Effect of Termination</h3>
              <p>
                Upon termination, your right to use the Services ceases immediately. You will continue to have access 
                to your account and data until the end of your paid billing period (for subscriptions) or until 
                account deletion is completed. We may delete your account data after a reasonable period (typically 
                30-90 days), subject to legal requirements and our data retention policy. You are responsible for 
                exporting any data you wish to retain before account deletion.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Intellectual Property</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                The Services and all content, features, and functionality are owned by SmartMetrics and are protected 
                by copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                You may not copy, modify, distribute, sell, or lease any part of our Services without our written permission.
              </p>
            </div>
          </section>

          {/* Disclaimers and Limitations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimers and Limitations of Liability</h2>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">10.1 Service Disclaimers</h3>
              <p>
                THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, 
                EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">10.2 Limitation of Liability</h3>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">10.3 Maximum Liability</h3>
              <p>
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICES IN THE 12 MONTHS 
                PRECEDING THE CLAIM.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                You agree to indemnify and hold harmless SmartMetrics from any claims, damages, or expenses arising from 
                your use of the Services or violation of these Terms.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law and Disputes</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                These Terms are governed by the laws of [Your Jurisdiction]. Any disputes shall be resolved through 
                binding arbitration in accordance with the rules of [Arbitration Organization].
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                We may modify these Terms at any time. We will notify users of material changes via email or through 
                our Services. Continued use after changes constitutes acceptance of the new Terms.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                If you have questions about these Terms, please contact us at:
              </p>
              <ul className="list-none pl-0 space-y-1">
                <li><strong>Email:</strong> support@smartmetrics.com</li>
              </ul>
            </div>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Severability</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will remain 
                in full force and effect.
              </p>
            </div>
          </section>

        </div>
      </div>
      <Footer />
    </div>
  );
}